import * as React from 'react';
import { graphql, useStaticQuery } from 'gatsby';

import type { CmsFileBase, CmsGlobalFileNode } from 'src/helpers/cms-file-helper';
import { findCmsFileNodeForBrand, parseRelativeDirectory } from 'src/helpers/cms-file-helper';
import { SiteContext } from 'src/context/site-context';
import { useMemo } from 'react';

const careerCmsContentQuery = graphql`
  query CareerCmsContentQuery {
    careerHeaderContent: allFile(filter: { base: { eq: "career-header.json" } }) {
      nodes {
        relativeDirectory
        childGlobalJson {
          ..._CareerHeaderContent
        }
      }
    }
  }
`;

interface WrappedGlobalCmsRecord<T> {
  careerHeaderContent: T;
}

export type CareerHeaderContent = {
  careerHeader: {
    headerMenuItems?: Queries.HeaderLevelOneMenuItem[];
    headerSecondaryUpperMenu?: Queries.HeaderSecondaryUpperMenuItem[];
    headerButtonPrimary?: Queries.RedesignButton;
    headerButtonSecondary?: Queries.RedesignButton;
    headerOtherText?: Queries.HeaderOtherText;
  };
};

export interface CareerCmsRecord {
  careerHeaderContent: CareerHeaderContent;
}

type CmsTemplateFileNode<T> = CmsFileBase & {
  childTemplateAppPromoJson?: T;
  childTemplateBlogJson?: T;
  childTemplateMembershipContentJson?: T;
  childTemplateWhyJoinListJson?: T;
};

type CmsGlobalRecordNode<T> = CmsGlobalFileNode<T> | CmsTemplateFileNode<T>;

const childJsonKeys = ['childGlobalJson'] as const;

const extractChildFromNode = <T,>(node: CmsGlobalRecordNode<T>) => {
  const childKey = childJsonKeys.find((key) => {
    if (key in node) {
      return true;
    }
  })!;
  // @ts-ignore
  return node[childKey];
};

export const CareerCmsProvider = ({
  children,
}: {
  children: (cmsContent: CareerCmsRecord) => React.ReactNode;
}) => {
  const { language, buildType } = React.useContext(SiteContext);

  const cms =
    useStaticQuery<WrappedGlobalCmsRecord<{ nodes: CmsGlobalRecordNode<any>[] }>>(
      careerCmsContentQuery
    );

  const cmsRecord = useMemo(() => {
    (Object.keys(cms) as (keyof typeof cms)[]).forEach((key) => {
      cms[key].nodes.forEach(parseRelativeDirectory);
    });

    return (Object.keys(cms) as (keyof typeof cms)[]).reduce((record, key) => {
      const cmsNode = findCmsFileNodeForBrand(cms[key].nodes, language, buildType);

      record[key] = cmsNode ? extractChildFromNode(cmsNode) : null;

      return record;
    }, {} as CareerCmsRecord);
  }, [buildType, cms, language]);

  return children(cmsRecord);
};
